import { useContext, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { sxBody, sxHeading, sxSection } from "../CandidateAccordions";
import { CandidateContext } from "../../../CandidateProfile";
import CandidateTags from "./CandidateTags";
import { useUserProfileStore } from "../../../../App/State/Stores/UserProfileStore";
import {
  CANDIDATE_STATUS_DEFAULTS,
  CandidateStatusIcon,
  CandidateStatusPicker,
} from "../../../../App/Components/CandidateStatus";
import { CandidateStatusColor } from "../../../../App/Enums/CandidateStatusColor";
import { useCandidateStatusDefinitionsQuery } from "../../../../App/Queries/CandidateStatusQueries";

const CandidateStatusDetails = ({
  candidateId,
  statusColor,
}: {
  candidateId: string;
  statusColor: string;
}) => {
  const { data: definitions } = useCandidateStatusDefinitionsQuery();
  const [pickerOpen, setPickerOpen] = useState(false);

  const source = definitions && definitions.length ? definitions : CANDIDATE_STATUS_DEFAULTS;
  const definition = source.find((d) => d.color === statusColor);
  const label = definition?.label ?? statusColor;
  const description = definition?.description?.trim();

  return (
    <>
      <Grid
        container
        alignItems="flex-start"
        onClick={() => setPickerOpen(true)}
        data-test="candidate-status-details"
        sx={{ cursor: "pointer" }}
      >
        <Grid item xs={11} sx={{ paddingRight: "0.5ch" }}>
          <Typography component="span" sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
          {description && (
            <Typography component="span" color="text.secondary">
              {` - ${description}`}
            </Typography>
          )}
        </Grid>
        <Grid item xs={1} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <CandidateStatusIcon statusColor={statusColor} />
        </Grid>
      </Grid>
      <CandidateStatusPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        candidateId={candidateId}
        currentStatusColor={statusColor}
      />
    </>
  );
};

export function CandidateDetails() {
  const { candidate } = useContext(CandidateContext);
  const { getUserAdjustedDate } = useUserProfileStore();

  const details = [
    {
      type: "text",
      heading: "Date Created:",
      content: getUserAdjustedDate(candidate.createdDate).format("MM/DD/YYYY h:mm A"),
      headingTestData: "candidate-header-createdDate",
      contentTestData: "candidate-createdDate",
    },
    {
      type: "text",
      heading: "Last Interaction:",
      content: getUserAdjustedDate(candidate.lastInteractedWithDate).format("MM/DD/YYYY h:mm A"),
      headingTestData: "candidate-header-lastInteraction",
      contentTestData: "candidate-lastInteraction",
    },
    {
      type: "text",
      heading: "Status:",
      content: (
        <CandidateStatusDetails
          candidateId={candidate.id}
          statusColor={candidate.statusColor ?? CandidateStatusColor.White}
        />
      ),
      headingTestData: "candidate-header-status",
      contentTestData: "candidate-status",
    },
    {
      type: "list",
      heading: "Tags:",
      content: <CandidateTags candidate={candidate} optimisticLoading={false} />,
      headingTestData: "candidate-header-tags",
      contentTestData: "candidate-tags",
    },
  ];

  return (
    <Box>
      {details.map((item, idx) => (
        <Box key={`candidate-details-${idx}`} sx={sxSection}>
          <Typography data-test={item.headingTestData} sx={sxHeading}>
            {item.heading}
          </Typography>
          {item.type === "text" && (
            <Typography data-test={item.contentTestData} sx={sxBody}>
              {item.content}
            </Typography>
          )}
          {item.type === "list" && (
            <Box
              data-test={item.contentTestData}
              sx={{
                ...sxBody,
                display: "flex",
                flexWrap: "wrap",
                gap: "0.5ch",
              }}
            >
              {item.content}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}
